// Pure-function layout for the ConstellationBanner dots. Maps an array of
// {lat, lng} points to {x, y} pixel coordinates inside a fixed viewport,
// preserving relative geographic distances.
//
// Algorithm: convert each point to local meters relative to the centroid
// (equirectangular projection - fine for sub-100m scales), then scale
// uniformly so the largest absolute offset just fits within the padded
// viewport.

const METERS_PER_DEG_LAT = 110540;

function centroid(points) {
  const sumLat = points.reduce((s, p) => s + p.lat, 0);
  const sumLng = points.reduce((s, p) => s + p.lng, 0);
  return { lat: sumLat / points.length, lng: sumLng / points.length };
}

/**
 * @param {Array<{lat:number,lng:number}>} points
 * @param {{width:number,height:number,padding:number}} viewport
 * @returns {Array<{x:number,y:number}>}
 */
export function layoutDots(points, viewport) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const { width, height, padding } = viewport;
  const cx = width / 2;
  const cy = height / 2;

  if (points.length === 1) {
    return [{ x: cx, y: cy }];
  }

  const c = centroid(points);
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos((c.lat * Math.PI) / 180);
  const localXY = points.map((p) => ({
    x: (p.lng - c.lng) * metersPerDegLng,
    y: (p.lat - c.lat) * METERS_PER_DEG_LAT,
  }));

  const maxAbs = Math.max(
    1e-6,
    ...localXY.flatMap((p) => [Math.abs(p.x), Math.abs(p.y)]),
  );

  const availableHalfWidth = width / 2 - padding;
  const availableHalfHeight = height / 2 - padding;
  const scale = Math.min(availableHalfWidth, availableHalfHeight) / maxAbs;

  return localXY.map((p) => ({
    x: cx + p.x * scale,
    // y axis is flipped: north is up but pixel y grows downward
    y: cy - p.y * scale,
  }));
}
